import { IcebergButton } from '@tradesolution/iceberg-ui-react';
import { useEffect, useState } from 'react';
import { Alert, Form, Modal } from 'react-bootstrap';
import { RegistrerCommand } from 'services/QAApi/types';
import QAApi from 'services/QAApi';
import { KjedeTreNode } from 'components/KjedePicker';
import KjedeApi from 'services/KjederegisteretAdminApi/KjedeApi';
import { useMsal } from '@azure/msal-react';
import LeftColumnForm from './LeftColumnForm';
import RightColumnForm from './RightColumnForm';
import { FormData, Kjede } from './types';
import { handleSmartPaste } from './smartPasteHandler';

const NewChangeRequestModal = () => {
  const { accounts } = useMsal();

  const username = accounts[0] && accounts[0].name;
  const epost = accounts[0] && accounts[0].username;

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    gln: '',
    organisasjonsnummer: '',
    telefonnummer: '',
    besoksadresse: '',
    nedlagtdato: null,
    enhetnavn: '',
    butikknummer: '',
    firmanavn: '',
    besokpostnummer: '',
    besokpoststed: '',
    fakturaadresse: '',
    fakturapostnummer: '',
    fakturapoststed: '',
    postadresse: '',
    postnummer: '',
    postpoststed: '',
    kommentar: '',
    leverandorkundenummer: '',
    leverandorkundenummerhos: '',
    kundenummerhossgrossist: '',
    bransjeforslag: '',
    kontonummer: '',
    salgsareal: 0,
    fakturaEpost: '',
    epost: '',
    oppstartsdato: null,
    omsetning: null,
    medlemHos: '',
    medlemsNummer: '',
    medlemFraOgMedDato: null,
    medlemTilOgMedDato: null,
    dagligLeder: '',
  });

  const handleCloseModal = () => {
    setShowModal(false);
    handleResetForm();
  };

  //Reset formfields
  const handleResetForm = () => {
    setFormData({
      gln: '',
      organisasjonsnummer: '',
      telefonnummer: '',
      besoksadresse: '',
      nedlagtdato: null,
      enhetnavn: '',
      butikknummer: '',
      firmanavn: '',
      besokpostnummer: '',
      besokpoststed: '',
      fakturaadresse: '',
      fakturapostnummer: '',
      fakturapoststed: '',
      postadresse: '',
      postnummer: '',
      postpoststed: '',
      kommentar: '',
      leverandorkundenummer: '',
      leverandorkundenummerhos: '',
      kundenummerhossgrossist: '',
      bransjeforslag: '',
      kontonummer: '',
      salgsareal: null,
      fakturaEpost: '',
      epost: '',
      oppstartsdato: null,
      omsetning: null,
      markedskjede: '',
      markedskjedeNr: '',
      regionalkjede: '',
      regionalkjedeNr: '',
      medlemHos: '',
      medlemsNummer: '',
      medlemFraOgMedDato: null,
      medlemTilOgMedDato: null,
    });

    //Clear alerts
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
  };

  const getRegionKjeder = async () => {
    try {
      const response = await KjedeApi.getRegionTre();
      return response;
    } catch (error) {
      console.error('Error fetching region chains:', error);
      throw new Error('Failed to fetch region chains');
    }
  };

  const getMarkedKjeder = async () => {
    try {
      const response = await KjedeApi.getMarkedsTre();
      return response;
    } catch (error) {
      console.error('Error fetching marked chains:', error);
      throw new Error('Failed to fetch marked chains');
    }
  };

  const [markedKjeder, setMarkedKjeder] = useState<KjedeTreNode[]>([]);
  const [regionKjeder, setRegionKjeder] = useState<KjedeTreNode[]>([]);

  const [mappedMarkedKjeder, setMappedMarkedKjeder] = useState<Kjede[]>([]);
  const [mappedRegionKjeder, setMappedRegionKjeder] = useState<Kjede[]>([]);

  useEffect(() => {
    const fetchKjeder = async () => {
      const [markedKjeder, regionKjeder] = await Promise.all([
        getMarkedKjeder(),
        getRegionKjeder(),
      ]);
      setMarkedKjeder(markedKjeder);
      setRegionKjeder(regionKjeder);

      // map markedKjeder and regionKjeder to the format expected by the ai prompt
      const mappedRegion = regionKjeder?.map((kjede) => ({
        id: kjede.id,
        name: kjede.name,
        kjedenr: kjede.kjedenr.toString(),
        enhetId: parseInt(kjede.enhetId),
        children: kjede.children?.map((c) => ({
          id: c.id,
          name: c.name,
          kjedenr: c.kjedenr.toString(),
          enhetId: c.enhetId,
          children: c.children?.map((c2) => ({
            id: c2.id,
            name: c2.name,
            kjedenr: c2.kjedenr.toString(),
            enhetId: c2.enhetId,
            children: c2.children?.map((c3) => ({
              id: c3.id,
              name: c3.name,
              kjedenr: c3.kjedenr.toString(),
              enhetId: c3.enhetId,
              children: [],
            })),
          })),
        })),
      }));

      setMappedRegionKjeder(mappedRegion);

      const mappedMarked = markedKjeder?.map((kjede) => ({
        id: kjede.id,
        name: kjede.name,
        kjedenr: kjede.kjedenr.toString(),
        enhetId: parseInt(kjede.enhetId),
        children: kjede.children?.map((c) => ({
          id: c.id,
          name: c.name,
          kjedenr: c.kjedenr.toString(),
          enhetId: c.enhetId,
          children: c.children?.map((c2) => ({
            id: c2.id,
            name: c2.name,
            kjedenr: c2.kjedenr.toString(),
            enhetId: c2.enhetId,
            children: c2.children?.map((c3) => ({
              id: c3.id,
              name: c3.name,
              kjedenr: c3.kjedenr.toString(),
              enhetId: c3.enhetId,
              children: [],
            })),
          })),
        })),
      }));

      setMappedMarkedKjeder(mappedMarked);
    };

    fetchKjeder();
  }, []);

  const [smartPasteLoading, setSmartPasteLoading] = useState(false);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSmartPasteClick = async () => {
    setSmartPasteLoading(true);

    try {
      const result = await handleSmartPaste(mappedMarkedKjeder, mappedRegionKjeder, markedKjeder);

      setFormData(result.formData);
    } catch (error) {
      console.error('Smart paste failed:', error);
    } finally {
      setSmartPasteLoading(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const command: RegistrerCommand = {
      senderId: '03c86c71-5024-4944-84bd-240da31be57e',
      contactEmail: epost,
      description: 'Endringsmelding fra KRAdmin (smartpaste)',
      pushNotificationUrl: '',
      sentBy: username,
      sentBySystem: 'Smartpaste',
      enhetInfo: {
        nedlagtDato: formData.nedlagtdato,
        enhetNavn: formData.enhetnavn,
        oppstartsdato: formData.oppstartsdato,
        gln: formData.gln,
        firmaNavn: formData.firmanavn,
        organisasjonsnummer: formData.organisasjonsnummer,
        besokAdresse: formData.besoksadresse,
        besokPostnummer: formData.besokpostnummer,
        besokPoststed: formData.besokpoststed,
        // Telefon: null if empty/invalid, trimmed value if valid phone format
        telefon:
          formData.telefonnummer && /^[\d\s\+\-\(\)]+$/.test(formData.telefonnummer.trim())
            ? formData.telefonnummer.trim()
            : null,
        fakturaAdresse: formData.fakturaadresse,
        fakturaPostnummer: formData.fakturapostnummer,
        fakturaPoststed: formData.fakturapoststed,
        postAdresse: formData.postadresse,
        postnummer: formData.postnummer,
        postPoststed: formData.postpoststed,
        kommentar: formData.kommentar,
        leverandorKundenummer: formData.leverandorkundenummer,
        leverandorKundenummerHos: formData.leverandorkundenummerhos,
        leverandorKundenummerHosTsKundeId: null,
        bransjeForslag: formData.bransjeforslag,
        kontonummer: formData.kontonummer,
        grossistKundenummer: formData.kundenummerhossgrossist,
        grossistKundenummerHos: '',
        grossistKundenummerHosGln: '',
        salgsareal: formData.salgsareal,
        fakturaEpost: formData.fakturaEpost,
        epost: formData.epost,
        //kjede fields
        markedskjede: formData.markedskjede,
        markedskjedeNr: formData.markedskjedeNr,
        regionalkjede: formData.regionalkjede,
        regionalkjedeNr: formData.regionalkjedeNr,
        omsetning: formData.omsetning,
        medlemHos: formData.medlemHos,
        medlemsNummer: formData.medlemsNummer,
        medlemFraOgMedDato: formData.medlemFraOgMedDato,
        medlemTilOgMedDato: formData.medlemTilOgMedDato,
        dagligLeder: formData.dagligLeder,
      },
    };
    try {
      const response = await QAApi.registrerEndringsmelding(command);

      //store the id of the newly created endringsmelding
      setNyEndringsmeldingId(response.id);

      setShowSuccessAlert(true);
      setShowErrorAlert(false);
    } catch (error) {
      console.error('Error oppstod av å sende endringsmelding:', error);

      setErrorMessage('Noe gikk galt! Vennligst prøv igjen.');
      setShowErrorAlert(true);
      setShowSuccessAlert(false);
    }
  };

  const [nyEndringsmeldingId, setNyEndringsmeldingId] = useState<string | null>(null);

  return (
    <>
      <IcebergButton
        className="w-100"
        icon="plus"
        variant="outline-primary"
        onClick={() => setShowModal(true)}
      >
        Ny endringsmelding
      </IcebergButton>
      <Modal show={showModal} onHide={handleCloseModal} centered size="xl">
        <Modal.Header closeButton>
          <Modal.Title>Ny endringsmelding</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 d-flex justify-content-end">
            <IcebergButton
              variant="outline-primary"
              icon="boxed-lightbulb"
              onClick={handleSmartPasteClick}
              isLoading={smartPasteLoading}
            >
              Smart Paste
            </IcebergButton>
          </div>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <LeftColumnForm formData={formData} handleChange={handleChange} />
              </div>
              <div className="col-md-6">
                <RightColumnForm formData={formData} handleChange={handleChange} />
              </div>
            </div>
            <div className="d-flex justify-content-between">
              <IcebergButton
                variant="outline-primary"
                icon="boxed-lightbulb"
                onClick={handleSmartPasteClick}
                isLoading={smartPasteLoading}
              >
                Smart Paste
              </IcebergButton>
              <IcebergButton
                variant="outline-primary"
                icon="rotate-right"
                onClick={handleResetForm}
                type="button"
              >
                Nullstill
              </IcebergButton>
            </div>
          </Form>

          {showSuccessAlert && (
            <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible>
              <div>
                <span>Alt gikk bra!</span> Endringsmeldingen ble sendt inn til QA.
                <br />
                <a href={`/qa?q=${nyEndringsmeldingId}`}>
                  Trykk her for å se opprettet endringsmelding
                </a>
              </div>
            </Alert>
          )}
          {showErrorAlert && (
            <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible>
              {errorMessage}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <IcebergButton
            className="col"
            icon="close"
            type="button"
            variant="secondary"
            onClick={handleCloseModal}
          >
            Lukk
          </IcebergButton>
          <IcebergButton
            className="col"
            icon="send"
            type="button"
            variant="primary"
            onClick={handleSubmit}
          >
            Send inn til QA
          </IcebergButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NewChangeRequestModal;
